import { apiClient } from './apiClient';
import { ConfigTemplateAssignRequest, ConfigTemplateAssignResult } from '../types/configTemplate';

export interface ValidTypeOptions {
  hardware_types: string[];
}

export interface HardwareType {
  id: number;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  ref_anep?: string[];
  reboot_payload?: string;
  /** The product's base configuration (schema v3.1): the first layer of every device of this type. */
  config?: Record<string, unknown>;
  is_default: boolean;
  is_active: boolean;
  device_count: number;
  created_at: string;
  updated_at: string;
  /** Present when the list was asked for `include_model_refs`. */
  model_refs?: ModelReference[];
}

export interface HardwareTypeStats {
  total_types: number;
  active_types: number;
  inactive_types: number;
  total_model_refs: number;
  active_model_refs: number;
}

export interface CustomFieldDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean';
  default: string | number | boolean;
  group?: string;
  notes?: string;
}

export interface HardwareTypeCreate {
  code: string;
  name: string;
  description?: string;
  icon?: string;
  ref_anep?: string[];
  reboot_payload?: string;
  client_id: string;
}

export interface HardwareTypeUpdate {
  code?: string;
  name?: string;
  description?: string;
  icon?: string;
  ref_anep?: string[];
  reboot_payload?: string;
  is_active?: boolean;
  config?: Record<string, unknown>;
}

// Model Reference interfaces
export interface ModelReference {
  id: number;
  hardware_type_id: number;
  code: string;
  name?: string;
  description?: string;
  display_order: number;
  is_active: boolean;
  client_ids?: string[];  // Multi-client support
  device_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ModelReferenceCreate {
  code: string;
  name?: string;
  description?: string;
  display_order?: number;
  client_ids?: string[];  // Multi-client support
}

export interface ModelReferenceUpdate {
  code?: string;
  name?: string;
  description?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface ModelReferenceListResponse {
  items: ModelReference[];
  total: number;
}

// Field Grouping interfaces
export interface FieldGrouping {
  id: number;
  code: string;
  name: string;
  description?: string;
  display_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}



// Field Definition interfaces
//
// Note on `select` vs `dropdown`:
//   - The backend's Pydantic FieldDefinitionCreate model accepts 'select'
//     as the dropdown value type (see provisioningv2 app/models/field_definition.py).
//   - Earlier frontend code used 'dropdown' as a display synonym; both
//     remain in the union for backward compat with local-only call sites
//     (custom-field editors, rendering helpers) but the **hardware type
//     Add Field form** must always send 'select' so the backend accepts it.
//   - 'email', 'ip_address', 'json' are frontend-only rendering hints and
//     are NOT valid for /v2/hardware-types/{id}/field-defs — they'll 422
//     if sent to that endpoint. Use them only for in-browser custom fields.
export type ValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'password'
  | 'url'
  | 'hostname'
  | 'email'
  | 'ip_address'
  | 'port'
  | 'select'
  | 'dropdown'
  | 'json'
  | 'array';

export interface FieldDefinition {
  id: number;
  config_type_id?: number;
  hardware_type_id?: number;
  grouping_id?: number;
  grouping_code?: string;
  grouping_name?: string;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string; // JSON string of dropdown options
  display_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  grouping?: FieldGrouping;
}

// Hardware-type specific field definitions
export interface HardwareFieldDefinition {
  id: number;
  hardware_type_id: number;
  grouping_id?: number;
  grouping_code?: string;
  grouping_name?: string;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string;
  display_order: number;
  is_system: boolean;
  expose_on_wire?: boolean;
  wire_root?: boolean;
  created_at: string;
  updated_at: string;
}



export interface HardwareFieldDefinitionListResponse {
  items: HardwareFieldDefinition[];
  total: number;
  hardware_type_id: number;
  hardware_type_code: string;
}

// Bulk-apply input: a plain field object. All fields except field_name,
// display_name, and value_type are optional. default_value / options accept
// any scalar/array and will be stringified for the backend.
export interface HardwareFieldDefinitionInput {
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: unknown;
  placeholder?: string;
  help_text?: string;
  is_required?: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: unknown;
  display_order?: number;
  grouping_id?: number;
  expose_on_wire?: boolean;
  wire_root?: boolean;
}

export interface ApplySchemaResult {
  created: string[];   // field_names created
  updated: string[];   // field_names updated
  deleted: string[];   // field_names deleted (only if deleteMissing=true)
  skipped: string[];   // field_names skipped (e.g. system fields not force-deleted)
  errors: Array<{ field_name: string; error: string }>;
}

export interface ApplySchemaOptions {
  deleteMissing?: boolean;
  forceDeleteSystem?: boolean;
}


export interface FieldDefinitionCreate {
  grouping_id?: number;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required?: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string;
  display_order?: number;
}

class TypesService {
  // Cache keyed by include_inactive flag so the active-only and
  // all-rows variants don't trample each other.
  private validOptionsCache: Map<boolean, ValidTypeOptions> = new Map();
  private hardwareTypesCache: HardwareType[] = [];
  // The stats request in flight or just answered: the page and its table both need it.
  private statsPromise: Promise<HardwareTypeStats> | null = null;
  private statsAt = 0;
  private lastFetch: Map<boolean, number> = new Map();
  private cacheDuration: number = 60000; // 1 minute

  async getValidOptions(
    includeInactive: boolean = false,
    forceRefresh: boolean = false,
  ): Promise<ValidTypeOptions> {
    const now = Date.now();
    const cached = this.validOptionsCache.get(includeInactive);
    const stamp = this.lastFetch.get(includeInactive) ?? 0;
    if (!forceRefresh && cached && now - stamp < this.cacheDuration) {
      return cached;
    }

    try {
      const url = includeInactive
        ? '/v2/types/valid-options?include_inactive=true'
        : '/v2/types/valid-options';
      const options = await apiClient.get(url);
      this.validOptionsCache.set(includeInactive, options);
      this.lastFetch.set(includeInactive, now);
      return options;
    } catch (error) {
      console.error('Failed to fetch valid type options:', error);
      // Return defaults if API fails
      return {
        hardware_types: ['UNKN', 'PGA', 'P4GU', 'IOTL', 'TMS2']
      };
    }
  }

  // ============================================================================
  // Hardware Types
  // ============================================================================

  /**
   * Every hardware type. With `withModelRefs` each type carries its
   * `model_refs`, so a screen that needs both makes one request instead of
   * one per type.
   */
  async getHardwareTypes(includeInactive: boolean = false, withModelRefs: boolean = false): Promise<HardwareType[]> {
    try {
      const params = new URLSearchParams();
      if (includeInactive) params.set('include_inactive', 'true');
      if (withModelRefs) params.set('include_model_refs', 'true');
      const url = params.toString() ? `/v2/hardware-types?${params.toString()}` : '/v2/hardware-types';
      const response = await apiClient.get(url);
      // Handle both array and paginated response formats
      const types = Array.isArray(response) ? response : (response.items || []);
      this.hardwareTypesCache = types;
      return types;
    } catch (error) {
      console.error('Failed to fetch hardware types:', error);
      return [];
    }
  }

  /** The model references of every type in one request, falling back to one request per type for an older API. */
  async getAllModelReferences(includeInactive: boolean = false): Promise<Array<{ type: HardwareType; refs: ModelReference[] }>> {
    const types = await this.getHardwareTypes(includeInactive, true);
    return Promise.all(types.map(async (type) => ({
      type,
      refs: Array.isArray(type.model_refs) ? type.model_refs : await this.getModelReferences(type.id, includeInactive),
    })));
  }

  /** Fleet-wide figures; concurrent callers share one request, answers are kept for 30 s. */
  getHardwareTypeStats(): Promise<HardwareTypeStats> {
    if (!this.statsPromise || Date.now() - this.statsAt > 30000) {
      this.statsAt = Date.now();
      this.statsPromise = apiClient.get('/v2/hardware-types/stats').catch((err) => { this.statsPromise = null; throw err; });
    }
    return this.statsPromise;
  }

  async getHardwareType(id: number): Promise<HardwareType | null> {
    try {
      return await apiClient.get(`/v2/hardware-types/${id}`);
    } catch (error) {
      console.error('Failed to fetch hardware type:', error);
      return null;
    }
  }

  async createHardwareType(data: HardwareTypeCreate): Promise<HardwareType> {
    const result = await apiClient.post('/v2/hardware-types', data);
    this.clearCache();
    return result;
  }

  async updateHardwareType(id: number, data: HardwareTypeUpdate): Promise<HardwareType> {
    const result = await apiClient.patch(`/v2/hardware-types/${id}`, data);
    this.clearCache();
    return result;
  }

  async deleteHardwareType(id: number): Promise<{ message: string }> {
    const result = await apiClient.delete(`/v2/hardware-types/${id}`);
    this.clearCache();
    return result;
  }

  // ============================================================================
  // Model References
  // ============================================================================

  async getModelReferences(hardwareTypeId: number, includeInactive: boolean = false): Promise<ModelReference[]> {
    try {
      const url = includeInactive
        ? `/v2/hardware-types/${hardwareTypeId}/model-refs?include_inactive=true`
        : `/v2/hardware-types/${hardwareTypeId}/model-refs`;
      const response = await apiClient.get(url);
      return response.items || [];
    } catch (error) {
      console.error('Failed to fetch model references:', error);
      return [];
    }
  }

  async createModelReference(hardwareTypeId: number, data: ModelReferenceCreate): Promise<ModelReference> {
    return await apiClient.post(`/v2/hardware-types/${hardwareTypeId}/model-refs`, data);
  }

  async updateModelReference(id: number, data: ModelReferenceUpdate): Promise<ModelReference> {
    return await apiClient.patch(`/v2/model-refs/${id}`, data);
  }

  async deleteModelReference(id: number): Promise<{ message: string }> {
    return await apiClient.delete(`/v2/model-refs/${id}`);
  }

  async reorderModelReferences(hardwareTypeId: number, order: number[]): Promise<ModelReferenceListResponse> {
    return await apiClient.put(`/v2/hardware-types/${hardwareTypeId}/model-refs/order`, { order });
  }

  // Model Reference Client Management
  async addModelReferenceClient(refId: number, clientId: string): Promise<{ message: string; client_ids: string[] }> {
    return await apiClient.post(`/v2/model-refs/${refId}/clients/${clientId}`, {});
  }

  async removeModelReferenceClient(refId: number, clientId: string): Promise<{ message: string; client_ids: string[] }> {
    return await apiClient.delete(`/v2/model-refs/${refId}/clients/${clientId}`);
  }

  async getModelReferenceClients(refId: number): Promise<string[]> {
    const result = await apiClient.get(`/v2/model-refs/${refId}/clients`);
    return result.client_ids || [];
  }

  // ============================================================================
  // Hardware Field Definitions
  // ============================================================================

  // ============================================================================
  // Field Groupings
  // ============================================================================

  // ============================================================================
  // Config Template assignment (SuperAdmin, schema v2)
  // ============================================================================

  /** Give a template to every device of its gateway type owned by the
   *  given clients, and / or to the given devices. */
  async assignConfigTemplate(templateId: number, body: ConfigTemplateAssignRequest): Promise<ConfigTemplateAssignResult> {
    return await apiClient.post(`/v2/config-templates/${templateId}/assign`, body);
  }

  // ============================================================================
  // Cache Management
  // ============================================================================

  getCachedHardwareTypes(): HardwareType[] {
    return this.hardwareTypesCache;
  }

  clearCache(): void {
    this.validOptionsCache.clear();
    this.hardwareTypesCache = [];
    this.statsPromise = null;
    this.lastFetch.clear();
  }
}

export const typesService = new TypesService();
